/**
 * The rules behind sending money back (0086), kept pure.
 *
 * A return is the only thing staff can do with money that arrived and cannot be
 * kept — a credit on a rail we never accept, or a wire from an account that is
 * not the client's own. These decide what to OFFER; the server decides what is
 * allowed.
 */
import type { BankTransaction, ReturnReason } from '../../services/bankTransactions.service';

/** Why money could not be kept, in the words a manager would use. */
export const RETURN_REASONS: ReadonlyArray<{ value: ReturnReason; label: string }> = [
  { value: 'rail_not_accepted', label: 'Arrived by a method we do not accept' },
  { value: 'sender_not_clients_own', label: 'Sent from someone else’s account' },
  { value: 'sender_unknown', label: 'We cannot tell who sent it' },
  { value: 'blocked_country', label: 'From a country we cannot take money from' },
  { value: 'client_request', label: 'The client asked for it back' },
  { value: 'unidentified', label: 'Nobody identified it within 90 days' },
  { value: 'other', label: 'Something else (say so below)' },
];

/**
 * Whether this movement can be sent back at all.
 *
 * Only an unattributed credit. Money already credited to a client is a balance,
 * and taking it back is the deposit-reversal path, which posts the compensating
 * debit under its own segregation of duties. Offering the button here would
 * promise a shortcut around that.
 */
export function canReturn(tx: Pick<BankTransaction, 'status'> & { direction?: string }): boolean {
  if (tx.direction !== undefined && tx.direction !== 'credit') return false;
  return tx.status === 'unmatched';
}

/** Why the return is not offered, said rather than hidden. */
export function whyCannotReturn(
  tx: Pick<BankTransaction, 'status'> & { direction?: string },
): string | null {
  if (tx.direction !== undefined && tx.direction !== 'credit') {
    return 'Only money that came in can be sent back.';
  }
  switch (tx.status) {
    case 'unmatched':
      return null;
    case 'matched':
      return 'This is already a client’s deposit. Reverse it from the deposit-reversal queue instead.';
    case 'returned':
      return 'This has already been sent back.';
    case 'voided':
      return 'This row was voided — it is not real money.';
    default:
      return null;
  }
}

const _INSTRUCTION_MIN = 10;

export interface ReturnDraft {
  reason: ReturnReason | '';
  instruction: string;
  fee: string;
}

/** The server's own bounds, checked here so the button is not offered in vain. */
export function isReturnDraftComplete(draft: ReturnDraft): boolean {
  if (draft.reason === '') return false;
  if (draft.instruction.trim().length < _INSTRUCTION_MIN) return false;
  const fee = Number(draft.fee.trim() === '' ? '0' : draft.fee);
  return Number.isFinite(fee) && fee >= 0;
}

/**
 * What the sender will actually receive.
 *
 * The bank's charge comes out of the amount returned (decision 2026-09-22), so
 * a manager should see the number the client will see before they commit to it.
 * Returns null when either side is not a number yet.
 */
export function netReturned(amount: string, fee: string): number | null {
  // `Number('')` is 0, not NaN, so an empty amount would read as a free return.
  // An empty FEE is deliberately zero (no charge); an empty amount is no answer.
  if (amount.trim() === '') return null;
  const gross = Number(amount);
  const charge = Number(fee.trim() === '' ? '0' : fee);
  if (!Number.isFinite(gross) || !Number.isFinite(charge)) return null;
  return gross - charge;
}

/** A fee bigger than the money is a typo, not a return. */
export function feeExceedsAmount(amount: string, fee: string): boolean {
  const net = netReturned(amount, fee);
  return net !== null && net < 0;
}

/** What the screen says happened, including the case with nobody to write to. */
export function describeReturnOutcome(outcome: {
  client_identified: boolean;
  client_notified: boolean;
}): string {
  if (outcome.client_notified) return 'Sent back, and the sender has been told what it costs them.';
  if (!outcome.client_identified) {
    return 'Sent back. The money came from an account nobody registered, so there is no one to write to — that is recorded.';
  }
  return 'Sent back. The notice to the sender could not be delivered; it is recorded and alerted.';
}
